import Axios from 'api/client';
import { AddressType } from 'constants/addresses';
import qs from 'qs';
import {
    Account,
    AccountInvite,
    AccountMember,
    AccountMemberOrInvite,
    AccountMemberRoleId,
    AccountMemberRoleInfo,
    AccountMenu,
    Address,
    CrmMember,
    LetterFulfillment,
    MiniAccount,
    PermissionSimulator,
    StripeAccount,
} from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface FetchAccountAddressesParams {
    page?: number;
    limit?: number;
    type?: string;
    not_type?: string;
}
interface AddressParams {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    isPrimary?: boolean;
    type?: string;
    name?: string;
}

interface InviteUserParams {
    email: string;
    role?: string;
    roles?: AccountMemberRoleId[];
    campaign_ids?: number[] | null;
    add_to_future_campaigns?: boolean;
    first_name?: string;
    last_name?: string;
}

interface EditUserParams {
    roles: AccountMemberRoleId[];
    campaign_ids?: number[] | null;
    add_to_future_campaigns?: boolean;
}

interface PermissionSimulatorParams {
    roles: AccountMemberRoleId[];
    permission_overrides?: { [key: string]: number }[];
}

interface CloseAccountParams {
    reason: string;
    feedback: string;
}

interface AccountResponse {
    data: Account;
    menu: AccountMenu;
}

const fetchAccount = (accountId: number) =>
    Axios.get<AccountResponse>(`/user/accounts/${accountId}`).then(response => response.data);

const fetchAccounts = () =>
    Axios.get<MiniAccount[]>(`/user/accounts`).then(response => response.data);

const fetchAccountAddresses = (accountId: number, params?: FetchAccountAddressesParams) =>
    Axios.get<PaginatedData<Address>>(`/accounts/${accountId}/addresses`, { params }).then(
        response => response.data,
    );

const fetchAccountAddress = (accountId: number, addressId: number) =>
    Axios.get<Address>(`/accounts/${accountId}/addresses/${addressId}`).then(
        response => response.data,
    );

interface OnboardingAction {
    id: number;
    action_item: string;
    completed_at: string | null;
    label: string;
    is_required: boolean;
}
type OnboardingActionsResponse = OnboardingAction[];
const fetchAccountOnboardingActions = (accountId: number) =>
    Axios.get<OnboardingActionsResponse>(`/accounts/${accountId}/onboarding-action-items`).then(
        response => response.data,
    );

const updateAccount = (accountId: number, params: Partial<Account & AccountMenu>) =>
    Axios.patch(`/accounts/${accountId}`, params).then(response => response.data);

const deleteAccount = (accountId: number) => Axios.delete(`/accounts/${accountId}`);

const closeAccount = (accountId: number, params: CloseAccountParams) =>
    Axios.post(`accounts/${accountId}/close`, params).then(response => response.data);

const createAccountAddress = (accountId: number, address: AddressParams) =>
    Axios.post<Address>(`/accounts/${accountId}/addresses`, {
        ...transformAddressToRequest(address),
        type: address.type ?? AddressType['Work'],
    }).then(response => response.data);

const updateAccountAddress = (
    accountId: number,
    addressId: number,
    address: Partial<AddressParams>,
) =>
    Axios.patch<Address>(`/accounts/${accountId}/addresses/${addressId}`, {
        ...transformAddressToRequest(address),
    }).then(response => response.data);

const deleteAccountAddress = (accountId: number, addressId: number) =>
    Axios.delete(`/accounts/${accountId}/addresses/${addressId}`);

const createEmailsRefer = (accountId: number, emails: string[]) =>
    Axios.post(`/accounts/${accountId}/refer-givebutter`, { emails });

const transformAddressToRequest = (address: Partial<AddressParams>) => ({
    address_1: address.line1,
    address_2: address.line2,
    city: address.city,
    state: address.state,
    zipcode: address.zip,
    country: address.country,
    is_primary: address.isPrimary ?? false,
    name: address.name,
});

const updateAccountMemberRole = (accountId: number, id: number, role: string) =>
    Axios.patch(`/accounts/${accountId}/members/${id}`, { role }).then(response => response.data);

const fetchAccountMember = (accountId: number, memberId: number) => {
    return Axios.get<AccountMember>(`/accounts/${accountId}/members/${memberId}`).then(
        response => response.data,
    );
};
const updateAccountMember = (accountId: number, id: number, data: EditUserParams) =>
    Axios.patch(`/accounts/${accountId}/members/${id}`, data).then(response => response.data);

const fetchAccountMembers = (accountId: number, params: FilterParams) => {
    const stringifiedFilters = params ? qs.stringify(params) : '';
    return Axios.get<PaginatedData<AccountMember>>(
        `/accounts/${accountId}/members?${stringifiedFilters}`,
    ).then(response => response.data);
};

const fetchAccountMembersWithInvites = (accountId: number, params: FilterParams) => {
    const { filters, ...rest } = params;
    return Axios.post<PaginatedData<AccountMemberOrInvite>>(
        `/accounts/${accountId}/members/filter?with_invites=1`,
        { ...filters, ...rest },
    ).then(response => response.data);
};

const fetchAccountMemberRoles = (accountId: number, params: FilterParams) => {
    return Axios.post<PaginatedData<AccountMemberRoleInfo>>(
        `/accounts/${accountId}/members/permissions/roles`,
        params,
    ).then(response => response.data);
};

const fetchAccountPermissionSimulator = (accountId: number, params: PermissionSimulatorParams) => {
    return Axios.post<PermissionSimulator>(
        `/accounts/${accountId}/members/permissions/simulate`,
        params,
    ).then(response => response.data);
};

const deleteAccountMember = (accountId: number, id: number) =>
    Axios.delete(`/accounts/${accountId}/members/${id}`).then(response => response.data);

const fetchCrmMembers = (accountId: number, params: { limit?: number; query?: string }) =>
    Axios.get<PaginatedData<CrmMember>>(`/accounts/${accountId}/crm_members`, { params }).then(
        response => response.data,
    );

const createAccountInvite = (accountId: number, invite: InviteUserParams) =>
    Axios.post(`/accounts/${accountId}/invites`, invite).then(response => response.data);

const fetchAccountInvite = (accountId: number, inviteId: string) => {
    return Axios.get<AccountInvite>(`/accounts/${accountId}/invites/${inviteId}`).then(
        response => response.data,
    );
};

const updateAccountInvite = (accountId: number, inviteId: string, params: EditUserParams) =>
    Axios.patch(`/accounts/${accountId}/invites/${inviteId}`, params).then(
        response => response.data,
    );

const fetchPendingUsers = (accountId: number, params: { page: number }) =>
    Axios.get(
        `/accounts/${accountId}/invites?filter=pending&role=editor,admin&page=${params.page}`,
    ).then(response => response.data);

const resendUserInvite = (accountId: number, id: string) =>
    Axios.post(`/accounts/${accountId}/invites/${id}/remind`).then(response => response.data);

const deleteUserInvite = (accountId: number, id: string) =>
    Axios.delete(`/accounts/${accountId}/invites/${id}`).then(response => response.data);

interface QRCodeResponse {
    qr_code: {
        id: number;
        name: string;
        call_to_action: string | null;
        custom_url: string | null;
        contact_id: number | null;
        campaign_id: number | null;
        campaign_title: string | null;
        color: string;
        background_color: string;
        total_scans: number;
        is_trackable: boolean;
        last_scanned_at: string | null;
        svg: string | null;
    };
    mail_piece?: LetterFulfillment;
}
interface QRCodeRequest {
    message_id: number;
    contact_id?: number;
    is_trackable?: boolean;
    campaign_id?: number;
    custom_url?: string;
    color?: string;
    background_color?: string;
    call_to_action?: string;
}
interface StripeBillingInformationRequest {
    billing_email: string;
}
const createAccountQRCode = (accountId: number, params: QRCodeRequest) =>
    Axios.post<QRCodeResponse>(`/accounts/${accountId}/qr_codes`, params).then(
        response => response.data,
    );

const fetchAccountQRCode = (accountId: number, qrCodeId: number) =>
    Axios.get<QRCodeResponse['qr_code']>(`/accounts/${accountId}/qr_codes/${qrCodeId}`).then(
        response => response.data,
    );

const updateAccountQRCode = (accountId: number, qrCodeId: number, params: QRCodeRequest) =>
    Axios.patch<QRCodeResponse['qr_code']>(
        `/accounts/${accountId}/qr_codes/${qrCodeId}`,
        params,
    ).then(response => response.data);

const fetchStripeExpressSession = (accountId: number) =>
    Axios.get<StripeAccount>(`/accounts/${accountId}/former-stripe-session`).then(
        ({ data }) => data,
    );

const fetchAccountSession = (accountId: number) =>
    Axios.get<StripeAccount>(`/accounts/${accountId}/treasury/account-session`).then(
        ({ data }) => data,
    );

const fetchAccountSessionSecure = (accountId: number) =>
    Axios.get<StripeAccount>(`/accounts/${accountId}/treasury/account-session-secure`).then(
        ({ data }) => data,
    );

const fetchAccountStripeNotificationsSession = (accountId: number) =>
    Axios.get<StripeAccount>(`/accounts/${accountId}/treasury/notification-session`).then(
        ({ data }) => data,
    );

const updateAccountStripeBillingInformation = (
    accountId: number,
    params: StripeBillingInformationRequest,
) =>
    Axios.patch(`/accounts/${accountId}/stripe-billing-information`, params).then(
        response => response.data,
    );

export {
    fetchAccount,
    fetchAccounts,
    updateAccount,
    deleteAccount,
    closeAccount,
    fetchAccountAddresses,
    fetchAccountAddress,
    fetchAccountOnboardingActions,
    createAccountAddress,
    updateAccountAddress,
    deleteAccountAddress,
    createEmailsRefer,
    transformAddressToRequest,
    createAccountInvite,
    fetchAccountInvite,
    updateAccountInvite,
    resendUserInvite,
    deleteUserInvite,
    deleteAccountMember,
    updateAccountMemberRole,
    fetchAccountMembers,
    fetchAccountMembersWithInvites,
    fetchAccountMemberRoles,
    fetchAccountPermissionSimulator,
    fetchCrmMembers,
    fetchPendingUsers,
    fetchAccountMember,
    updateAccountMember,
    createAccountQRCode,
    fetchAccountQRCode,
    updateAccountQRCode,
    updateAccountStripeBillingInformation,
    fetchStripeExpressSession,
    fetchAccountSession,
    fetchAccountStripeNotificationsSession,
    fetchAccountSessionSecure,
};

export type {
    AddressParams,
    AccountResponse,
    FetchAccountAddressesParams,
    OnboardingActionsResponse,
    PermissionSimulatorParams,
    QRCodeRequest,
    QRCodeResponse,
};
